import { Box, Section, Text } from '@factorialco/deprecated-design-system'
import { Switch } from '@factorialco/f0-react/dist/experimental'
import {
  SalaryAdvanceSettingsPageDocument,
  SaveSalaryAdvanceCompanySettingDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { normalizeCapPercentage } from 'modules/salary_advance/lib/companySettings'

type Props = {
  serviceEnabled: boolean
  requestDeadlineDay: number
  advanceCapMonthlyWagePercent: number
  canEdit: boolean
}

const ServiceEnablementSetting = ({
  serviceEnabled,
  requestDeadlineDay,
  advanceCapMonthlyWagePercent,
  canEdit,
}: Props) => {
  const [saveCompanySetting, { loading }] = useMutation(SaveSalaryAdvanceCompanySettingDocument, {
    extractorFn: (payload) =>
      payload.salaryAdvanceMutations?.saveSalaryadvanceCompanySetting ?? { errors: [] },
    refetchQueries: [SalaryAdvanceSettingsPageDocument],
  })

  const handleToggle = async (newValue: boolean) => {
    const result = await saveCompanySetting({
      variables: {
        serviceEnabled: newValue,
        requestDeadlineDay,
        advanceCapMonthlyWagePercent: normalizeCapPercentage(advanceCapMonthlyWagePercent),
      },
    })
    const handledResult = handleResult(result, () => {})

    track(trackingEvents.salaryAdvance.settingsSaveResult, {
      setting_key: 'service_enabled',
      setting_value_boolean: newValue,
      outcome: handledResult.success ? 'success' : 'failure',
    })

    if (handledResult.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  return (
    <Section.Form
      illustration="settings"
      title={i18n.t('salary_advance.settings.service_enablement.title')}
      description={i18n.t('salary_advance.settings.service_enablement.description_toggle')}
      isDirty={false}
    >
      <Box flexDirection="row" alignItems="center" gap="s12">
        <Text
          size="200"
          className={serviceEnabled ? 'text-f1-foreground-secondary' : 'text-f1-foreground'}
        >
          {i18n.t('salary_advance.settings.service_enablement.disabled_label')}
        </Text>
        <Switch
          id="service-enablement-toggle"
          checked={serviceEnabled}
          disabled={!canEdit || loading}
          onCheckedChange={handleToggle}
        />
        <Text
          size="200"
          className={serviceEnabled ? 'text-f1-foreground' : 'text-f1-foreground-secondary'}
        >
          {i18n.t('salary_advance.settings.service_enablement.enabled_label')}
        </Text>
      </Box>
    </Section.Form>
  )
}

export default ServiceEnablementSetting
